import {
  createFileRoute,
  redirect,
  retainSearchParams,
  stripSearchParams,
} from '@tanstack/react-router'
import { isEqual, pick } from 'lodash/fp'
import * as v from 'valibot'

import { AiAssistantWidget } from '@/ai-assistant/components/AiAssistantWidget/AiAssistantWidget'
import { FEATURE_AI_ASSISTANT } from '@/core/constants/features'
import { buildPageTitle } from '@/core/utils/buildPageTitle/buildPageTitle'
import useFeatures from '@/core/utils/features/useFeatures'
import { getLocalStorageItem } from '@/core/utils/localStorage/getLocalStorageItem'
import { setLocalStorageItem } from '@/core/utils/localStorage/setLocalStorageItem'
import { updateLocalStorageItem } from '@/core/utils/localStorage/updateLocalStorageItem'
import LearnpathsSummary from '@/learnpaths-summary/components/LearnpathsSummary/LearnpathsSummary'
import { DialogProvider } from '@/learnpaths-summary/contexts/Dialog'
import { useIsLearnpathCreatorAvailable } from '@/learnpaths/hooks/useIsLearnpathCreatorAvailable'
import { CONTENT_LOCALE } from '@/locale/constants'
import { NotFound } from '@/router/NotFound'

const preferencesStorageKey = 'managed-learnpaths-preferences'
const filtersStorageKey = 'managed-learnpaths-filters'
const preferencesKey = 'only_direct_learners_stats'

const searchSchema = v.object({
  [preferencesKey]: v.optional(v.boolean()),
  categoryId: v.optional(v.string()),
  group: v.optional(v.object({ id: v.string(), name: v.string() })),
  language: v.optional(v.picklist(CONTENT_LOCALE)),
  ownership: v.optional(v.picklist(['all', 'myself', 'my-learners'])),
  search: v.optional(v.string()),
  status: v.fallback(
    v.optional(v.picklist(['active', 'scheduled', 'draft', 'inactive'])),
    undefined,
  ),
  types: v.fallback(
    v.optional(v.array(v.picklist(['mandatory', 'linear', 'certificate']))),
    undefined,
  ),
  user: v.optional(v.string()),
})
const filterKeys = Object.keys(searchSchema.entries) as Array<
  keyof v.InferInput<typeof searchSchema>
>

const defaultPreferences = { [preferencesKey]: true }
const defaultFilters = {
  types: [] as ('mandatory' | 'linear' | 'certificate')[],
  ownership: 'all' as const,
}

export const Route = createFileRoute(
  '/company/_management/learnpaths/overview',
)({
  head: () => ({
    meta: [
      {
        title: buildPageTitle({
          titleKey: 'page-title-learnpaths-overview',
        }),
      },
    ],
  }),
  validateSearch: searchSchema,
  beforeLoad: ({ cause, location, search }) => {
    const storedFilters = getLocalStorageItem(filtersStorageKey)
    const storedPreferences = getLocalStorageItem(preferencesStorageKey)

    const searchFilters = {
      ...pick(['categoryId', 'group', 'language', 'status', 'user'], search),
      ownership: search.ownership ?? defaultFilters.ownership,
      types: search.types ?? defaultFilters.types,
    }

    if (cause === 'stay') {
      if (!isEqual(storedFilters, searchFilters)) {
        setLocalStorageItem(filtersStorageKey, searchFilters)
      }

      const currentPreference = search[preferencesKey] !== false
      if (storedPreferences?.[preferencesKey] !== currentPreference) {
        updateLocalStorageItem(preferencesStorageKey, {
          [preferencesKey]: currentPreference,
        })
      }
    }

    if (cause !== 'enter') return
    if (!location.state?.restoreFilters) return
    if (isEqual(storedFilters, searchFilters)) return

    const preferencesOverride =
      storedPreferences?.[preferencesKey] === false
        ? { [preferencesKey]: false as const }
        : {}

    throw redirect({
      to: '/company/learnpaths/overview',
      search: { ...storedFilters, ...preferencesOverride },
      replace: true,
    })
  },
  search: {
    middlewares: [
      stripSearchParams({ ...defaultFilters, ...defaultPreferences }),
      retainSearchParams(filterKeys),
    ],
  },

  component: RouteComponent,
})

function RouteComponent() {
  const isLearnpathEnabled = useIsLearnpathCreatorAvailable()
  const hasAiAssistant = useFeatures(FEATURE_AI_ASSISTANT)

  if (!isLearnpathEnabled) {
    return <NotFound />
  }

  return (
    <DialogProvider>
      <LearnpathsSummary />
      {hasAiAssistant && <AiAssistantWidget />}
    </DialogProvider>
  )
}
